import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CarGallery from "@/components/CarGallery";
import { marbellaCars } from "@/data/marbella-cars";

const ease = [0.22, 1, 0.36, 1] as const;

const MarbellaCarDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const car = marbellaCars.find((c) => c.slug === slug);

  if (!car) return <Navigate to="/drive/marbella" replace />;

  const getSpecValue = (label: string) =>
    car.specs.find((s) => s.label === label)?.value ?? "—";

  const galleryImages = car.gallery && car.gallery.length > 0 ? car.gallery : [car.image];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-[70vh] md:h-screen overflow-hidden">
        <CarGallery images={galleryImages} name={car.name} />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 via-transparent to-foreground/20 pointer-events-none" />
        <div className="absolute top-28 left-8 md:left-16 z-20">
          <Link
            to="/drive/marbella"
            className="font-label text-primary-foreground/50 text-[10px] tracking-[0.2em] uppercase hover:text-primary-foreground transition-colors duration-300"
          >
            ← Back to fleet
          </Link>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 px-8 md:px-16 lg:px-20 pb-16 md:pb-24">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1.5, ease }}
          >
            Marbella · Season 2026
          </motion.span>
          <motion.h1
            className="font-display text-3xl md:text-5xl lg:text-6xl text-primary-foreground leading-[1.05] tracking-wide uppercase"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1.5, ease }}
          >
            {car.name}
          </motion.h1>
        </div>
      </section>

      {/* Details */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left — Description + Specs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease }}
            >
              <span className="font-label text-bronze block mb-6">About this vehicle</span>
              <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-12">
                {car.description}
              </p>

              <div className="grid grid-cols-2 gap-y-8 gap-x-12">
                {["Passengers", "Engine", "Power", "0–100 km/h", "Top Speed"].map((label) => (
                  <div key={label}>
                    <span className="font-label text-muted-foreground/50 text-[10px] tracking-[0.15em] uppercase block mb-1">
                      {label}
                    </span>
                    <span className="font-display-medium text-foreground text-lg tracking-wide">
                      {getSpecValue(label)}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right — Pricing + CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.15, ease }}
            >
              <div className="border border-foreground/10 p-8 md:p-10">
                <span className="font-label text-bronze block mb-2">Rental · Season 2026</span>
                <h3 className="font-display text-2xl md:text-3xl text-foreground tracking-wide uppercase mb-6">
                  {car.price}
                </h3>
                <div className="space-y-3 mb-10">
                  {[
                    "Full insurance included",
                    "Delivery to your location",
                    "24/7 roadside assistance",
                    "Flexible rental periods",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-1 h-1 bg-bronze rounded-full" />
                      <span className="font-body text-muted-foreground text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-3">
                  <a
                    href="https://wa.me/message/2XEKJ5STR2IZJ1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-8 py-3 bg-foreground text-background text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground/90 transition-colors duration-300"
                  >
                    Reserve via WhatsApp
                  </a>
                  <a
                    href="mailto:info@arzanigroupworldwide.com"
                    className="block w-full text-center px-8 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
                  >
                    Email Us
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-4xl">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">How It Works</span>
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground tracking-wide uppercase">
              Vehicle Requests
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              { step: "1", title: "Choose your dates", text: "Select your preferred rental period and let us know where you'd like the vehicle delivered." },
              { step: "2", title: "Share your preferences", text: "Tell us about your plans so we can ensure the perfect vehicle and any additional arrangements." },
              { step: "3", title: "Enjoy the drive", text: "Your vehicle arrives fully prepared. Take the keys and explore the Costa del Sol at your own pace." },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center md:text-left"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.15, ease }}
              >
                <span className="font-display text-5xl md:text-6xl text-primary-foreground/10 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-lg text-primary-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-primary-foreground/50 text-sm leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Browse Fleet */}
      <section className="section-spacing bg-background">
        <div className="container-luxury text-center">
          <Link
            to="/drive/marbella"
            className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
          >
            Browse Full Fleet
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarbellaCarDetail;
